/**
 * AI CONTEXT
 * Purpose: two-step auth flow (manager credentials -> user PIN).
 * Key deps: AuthorizationContext, UserContext, useGetAllUsers, ROUTES.
 * Referenced by: App route /login and auth redirects.
 * Importance: app access gate for all protected screens.
 * Refactor: extract shared animation/login card + unify timeout/error handling.
 */
import { Form, Input, InputRef, Typography } from "antd"
import { OTPRef } from "antd/es/input/OTP"
import { AnimatePresence, motion } from "motion/react"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"

import Corgi from "#/assets/loading2.gif"
import CountdownDisplay from "#blocks/CountdownDisplay"
import Flex from "#blocks/Flex"
import SingleClickButton from "#blocks/SingleClickButton"
import { ROUTES } from "#constants/routes"
import { useAuthorizationContext } from "#contexts/AuthorizationContextProvider"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { useGetAllUsers } from "#hooks/newHooks/useGetUser"
import { getJarvisLocalStorage } from "#hooks/newHooks/useLocalStorage"
import usePinLockout from "#hooks/newHooks/usePinLockout"
import { getPinLockoutSecondsRemaining } from "#utils/pinLockoutHelpers"
import { ErrorType, formatError } from "#utils/textFormatters"

const SHAKE_ANIMATION = {
  shake: {
    x: [-10, 10, -10, 10, -5, 5, 0],
    transition: {
      duration: 0.4,
      ease: "easeInOut" as const,
    },
  },
  normal: {
    x: 0,
  },
}

const LoadingGif = () => {
  const theme = useThemeContext()

  return (
    <motion.div
      data-testid="login-loading"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      <Flex
        style={{
          background: `radial-gradient(circle at top right, ${theme.highlightColor} 0%, ${theme.backgroundColor} 100%)`,
          padding: "50px 40px",
          borderRadius: "20px",
        }}
      >
        <motion.div
          initial={{ opacity: 1, scale: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            background: `linear-gradient(135deg, ${theme.white} 0%, ${theme.body3} 100%)`,
            padding: "40px",
            borderRadius: "16px",
            boxShadow: `0 12px 40px rgba(0, 0, 0, 0.15)`,
            border: `1px solid ${theme.border}`,
            backdropFilter: "blur(10px)",
            position: "relative",
            height: 220,
            width: 320,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 20,
          }}
        >
          <img
            src={Corgi}
            alt="Corgi"
            style={{ width: "auto", height: 100, borderRadius: 10 }}
          />
          <Typography.Title
            level={3}
            style={{
              color: theme.gray[6],
              textAlign: "center",
            }}
          >
            Loading...
          </Typography.Title>
        </motion.div>
      </Flex>
    </motion.div>
  )
}

const PinLogin = () => {
  const { onPinLogin, isPinLoggedIn } = useUserContext()
  const theme = useThemeContext()
  const inputRef = useRef<OTPRef>(null)
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [shouldShake, setShouldShake] = useState(false)
  const [isSuccess, setIsSuccess] = useState(isPinLoggedIn)
  const [isFadingOut, setIsFadingOut] = useState(false)
  const [lastPage, setLastPage] = useState(ROUTES.FRONT_DESK_VIEW)
  const { isLockedOut, secondsRemaining, recordFailedAttempt, clearLockout } =
    usePinLockout()

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>
    let timeout2: ReturnType<typeof setTimeout>
    if (isSuccess) {
      // Show checkmark, then fade out, then redirect
      timeout = setTimeout(() => {
        setIsFadingOut(true)
        timeout2 = setTimeout(() => {
          navigate(lastPage)
        }, 500) // fade out duration
      }, 700) // checkmark duration
    }

    return () => {
      if (timeout) clearTimeout(timeout)
      if (timeout2) clearTimeout(timeout2)
    }
  }, [isSuccess, navigate, lastPage])

  if (isPinLoggedIn && !isSuccess) {
    // Prevent double navigation
    return null
  }

  const handlePinChange = async (pin: string) => {
    if (pin.length === 4) {
      if (isLockedOut) {
        form.setFields([
          {
            name: "pin",
            errors: [`Try again in ${secondsRemaining} seconds`],
          },
        ])
        form.resetFields(["pin"])
        return
      }

      try {
        const lastPage = await onPinLogin(pin)
        clearLockout()
        setLastPage(lastPage)
        setIsSuccess(true)
      } catch (error) {
        recordFailedAttempt()
        form.setFields([
          {
            name: "pin",
            value: "",
            errors: [error instanceof Error ? error.message : "Invalid pin"],
          },
        ])
        setShouldShake(true)
        setTimeout(() => {
          setShouldShake(false)
          const lockoutRemaining = getPinLockoutSecondsRemaining(
            getJarvisLocalStorage().pinLockoutUntilMs
          )
          if (lockoutRemaining === 0) {
            inputRef.current?.focus()
          }
        }, 1000)
      }
    } else {
      if (form.getFieldError("pin").length > 0) {
        form.setFields([{ name: "pin", errors: [] }])
      }
    }
  }

  return (
    <Flex col gap={10}>
      <Flex
        center
        style={{
          background: `radial-gradient(circle at top right, ${theme.highlightColor} 0%, ${theme.backgroundColor} 100%)`,
          padding: "50px 40px",
          borderRadius: "20px",
        }}
        col
        gap={30}
      >
        <motion.div
          variants={SHAKE_ANIMATION}
          initial={{ opacity: 1, scale: 1, x: 0 }}
          animate={
            shouldShake
              ? {
                  ...SHAKE_ANIMATION.shake,
                  opacity: isFadingOut ? 0 : 1,
                  scale: isFadingOut ? 0.95 : 1,
                }
              : {
                  ...SHAKE_ANIMATION.normal,
                  opacity: isFadingOut ? 0 : 1,
                  scale: isFadingOut ? 0.95 : 1,
                }
          }
          transition={{ duration: 0.5 }}
          style={{
            background: `linear-gradient(135deg, ${theme.white} 0%, ${theme.body3} 100%)`,
            padding: "40px",
            borderRadius: "16px",
            boxShadow: `0 12px 40px rgba(0, 0, 0, 0.15)`,
            border: `1px solid ${theme.border}`,
            backdropFilter: "blur(10px)",
            position: "relative",
            minHeight: 220,
            minWidth: 320,
          }}
        >
          {isSuccess ? (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 140,
              }}
            >
              <svg width="80" height="80" viewBox="0 0 80 80">
                <circle
                  cx="40"
                  cy="40"
                  r="38"
                  fill="#e6f9ec"
                  stroke="#27ae60"
                  strokeWidth="4"
                />
                <motion.path
                  d="M25 42 L37 54 L56 32"
                  fill="none"
                  stroke="#27ae60"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.5 }}
                />
              </svg>
            </motion.div>
          ) : isLockedOut ? (
            <Flex col center gap={12} style={{ minHeight: 140 }}>
              <Typography.Title
                level={2}
                style={{
                  margin: 0,
                  color: theme.mainColor,
                  textAlign: "center",
                }}
              >
                Too many attempts
              </Typography.Title>
              <CountdownDisplay
                data-testid="pin-lockout-countdown"
                label="Try again in"
                seconds={secondsRemaining}
              />
            </Flex>
          ) : (
            <>
              <Flex col style={{ width: "100%" }} gap={8}>
                <Typography.Title
                  level={2}
                  style={{
                    margin: 0,
                    color: theme.mainColor,
                    textAlign: "center",
                  }}
                >
                  Enter PIN
                </Typography.Title>
                <Typography.Text
                  style={{
                    color: theme.gray[6],
                    textAlign: "center",
                    fontSize: "16px",
                  }}
                >
                  Please enter your 4-digit PIN
                </Typography.Text>
              </Flex>
              <Form form={form} style={{ marginTop: "20px" }}>
                <Form.Item
                  name="pin"
                  style={{
                    marginBottom: 0,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Input.OTP
                    ref={inputRef}
                    data-testid="login-pin-input"
                    onChange={handlePinChange}
                    length={4}
                    mask={"*"}
                    disabled={isLockedOut}
                    style={{
                      fontSize: "18px",
                      width: 200,
                    }}
                  />
                </Form.Item>
              </Form>
            </>
          )}
        </motion.div>
      </Flex>
    </Flex>
  )
}

const ManagerLogin = () => {
  const { onManagerLogin } = useAuthorizationContext()
  const theme = useThemeContext()
  const [form] = Form.useForm()
  const inputRef = useRef<InputRef>(null)
  const [shouldShake, setShouldShake] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isFadingOut, setIsFadingOut] = useState(false)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  useEffect(() => {
    if (isSuccess) {
      setTimeout(() => {
        setIsFadingOut(true)
      }, 700)
    }
  }, [isSuccess])

  const handleSubmit = async (values: {
    username: string
    password: string
  }) => {
    try {
      await onManagerLogin(values)
      form.setFields([
        { name: "username", errors: [] },
        { name: "password", errors: [] },
      ])
      setIsSuccess(true)
    } catch (error) {
      const errorMessage = formatError(error as ErrorType)
      if (errorMessage.includes("invalid-email")) {
        form.setFields([
          { name: "username", errors: [] },
          { name: "password", errors: ["Invalid login"] },
        ])
      } else {
        form.setFields([
          { name: "username", errors: [] },
          { name: "password", errors: [errorMessage] },
        ])
      }
      setShouldShake(true)
      setTimeout(() => {
        form.setFields([{ name: "password", value: "" }])
        setTimeout(() => {
          setShouldShake(false)
        }, 10)
      }, 1000)
    }
  }

  return (
    <Flex
      center
      style={{
        background: `radial-gradient(circle at top right, ${theme.highlightColor} 0%, ${theme.backgroundColor} 100%)`,
        padding: "50px 40px",
        borderRadius: "20px",
      }}
      col
      gap={30}
    >
      <motion.div
        variants={SHAKE_ANIMATION}
        initial={{ opacity: 1, scale: 1, x: 0 }}
        animate={
          shouldShake
            ? {
                ...SHAKE_ANIMATION.shake,
                opacity: isFadingOut ? 0 : 1,
                scale: isFadingOut ? 0.95 : 1,
              }
            : {
                ...SHAKE_ANIMATION.normal,
                opacity: isFadingOut ? 0 : 1,
                scale: isFadingOut ? 0.95 : 1,
              }
        }
        transition={{ duration: 0.5 }}
        style={{
          background: `linear-gradient(135deg, ${theme.white} 0%, ${theme.body3} 100%)`,
          padding: "40px",
          borderRadius: "16px",
          boxShadow: `0 12px 40px rgba(0, 0, 0, 0.15)`,
          border: `1px solid ${theme.border}`,
          backdropFilter: "blur(10px)",
          position: "relative",
          minHeight: 260,
          minWidth: 320,
        }}
      >
        {isSuccess ? (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 400, damping: 20 }}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 140,
            }}
          >
            <svg width="80" height="80" viewBox="0 0 80 80">
              <circle
                cx="40"
                cy="40"
                r="38"
                fill="#e6f9ec"
                stroke="#27ae60"
                strokeWidth="4"
              />
              <motion.path
                d="M25 42 L37 54 L56 32"
                fill="none"
                stroke="#27ae60"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.5 }}
              />
            </svg>
          </motion.div>
        ) : (
          <>
            <Flex col style={{ width: "100%" }} gap={8}>
              <Typography.Title
                level={2}
                style={{
                  margin: 0,
                  color: theme.mainColor,
                  textAlign: "center",
                }}
              >
                Welcome!
              </Typography.Title>
              <Typography.Text
                style={{
                  color: theme.gray[6],
                  textAlign: "center",
                  fontSize: "16px",
                }}
              >
                Please log in to continue
              </Typography.Text>
            </Flex>
            <Form
              form={form}
              onFinish={handleSubmit}
              style={{ marginTop: "20px" }}
            >
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: "Please enter your username" },
                ]}
              >
                <Input
                  ref={inputRef}
                  placeholder="Enter username"
                  size="large"
                  autoComplete="username"
                  style={{
                    borderRadius: "8px",
                    backgroundColor: `${theme.body5}`,
                    border: `1px solid ${theme.border}`,
                  }}
                />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: "Please enter your password" },
                ]}
              >
                <Input.Password
                  placeholder="Enter password"
                  size="large"
                  autoComplete="current-password"
                  style={{
                    borderRadius: "8px",
                    backgroundColor: `${theme.body5}`,
                    border: `1px solid ${theme.border}`,
                  }}
                />
              </Form.Item>
              <SingleClickButton
                onClick={() => {
                  form.submit()
                }}
                type="primary"
                htmlType="submit"
                size="large"
                style={{
                  width: "100%",
                  height: "48px",
                  borderRadius: "8px",
                  backgroundColor: theme.mainColor,
                  border: "none",
                  fontSize: "16px",
                  fontWeight: "600",
                  boxShadow: `0 4px 12px rgba(0, 0, 0, 0.15)`,
                }}
              >
                Log in
              </SingleClickButton>
            </Form>
          </>
        )}
      </motion.div>
    </Flex>
  )
}

const Login = () => {
  const { isManagerLoggedIn } = useAuthorizationContext()
  const allUsers = useGetAllUsers()
  const isLoading = !allUsers?.length
  const theme = useThemeContext()

  return (
    <Flex
      style={{
        justifyContent: "center",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(135deg, ${theme.body2} 0%, ${theme.body3} 100%)`,
        width: "100vw",
        height: "100vh",
      }}
    >
      <Flex
        style={{
          marginTop: 100,
          justifyContent: "center",
          height: "fit-content",
        }}
      >
        <AnimatePresence mode="wait">
          {!isManagerLoggedIn ? (
            <ManagerLogin />
          ) : isLoading ? (
            <LoadingGif />
          ) : (
            <PinLogin />
          )}
        </AnimatePresence>
      </Flex>
    </Flex>
  )
}

export default Login
